import React, {useEffect, useMemo, useState} from "react";
import {createTheme, CssBaseline, PaletteMode, ThemeProvider, useMediaQuery} from "@mui/material";
import {BrowserRouter} from "react-router-dom";
import {PentaSplit} from "../common/PentaSplit";
import {Header} from "./Header";
import {Sidebar} from "./Sidebar";
import {RouterItems} from "../../pages/RouterItems";
import {GlobalState} from "../../global/GlobalState";
import {
	GlobalEvents, TargetStageChange,
	UserLoggedOn,
	UserPreferencesChange
} from "../../global/GlobalEvents";
import {ToastContainer} from "react-toastify";
import {LanguageEvents} from "../../localization/Translations";
import {TranslationChange} from "../../localization/models/TranslationChange";
import {Footer} from "./Footer";
import { SlotsProvider } from "nano-slots";
import {LoginPage} from "../../pages/login/LoginPage";

export function Main() {
	
	// refresh/re-render entire application when language is fetched
	const [, setLanguageHash] = useState<TranslationChange>();
	useEffect(() => {
		const handler = (event: TranslationChange) => setLanguageHash(event);
		LanguageEvents.onChange.on(handler);
		return function cleanup() {
			LanguageEvents.onChange.off(handler);
		};
	}, []);
	
	
	// refresh/re-render entire application when a user logged on
	const [, setUserSession] = useState(() => GlobalState.userSession);
	useEffect(() => {
		const handler = (event: UserLoggedOn) => setUserSession(event.userSession);
		GlobalEvents.userLoggedOn.on(handler);
		return function cleanup() {
			GlobalEvents.userLoggedOn.off(handler);
		};
	}, []);
	
	// refresh/re-render entire application when a user changes their preferences
	const [userPreferences, setUserPreferences] = useState(() => GlobalState.userPreferences);
	useEffect(() => {
		const handler = (event: UserPreferencesChange) => setUserPreferences(event.userPreferences);
		GlobalEvents.userPreferencesChanged.on(handler);
		return function cleanup() {
			GlobalEvents.userPreferencesChanged.off(handler);
		};
	}, []);
	
	// refresh/re-render entire application when a user changes the target stage
	const [, setTargetStage] = useState(() => GlobalState.targetStage);
	useEffect(() => {
		const handler = (event: TargetStageChange) => setTargetStage(event.targetStage);
		GlobalEvents.targetStageChanged.on(handler);
		return function cleanup() {
			GlobalEvents.targetStageChanged.off(handler);
		};
	}, []);
	
	const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
	let mode: PaletteMode;
	switch (userPreferences.colorTheme) {
		case "dark":
			mode = "dark";
			break;
		case "system-default":
			mode = prefersDarkMode ? "dark" : "light";
			break;
		case "light":
		case "none":
		default:
			mode = "light";
			break;
	}
	
	const theme = useMemo(
		() =>
			createTheme({
				palette: {
					mode: mode,
				}
			}),
		[mode],
	);
	GlobalState.paletteMode = mode;
	

	const params = new URLSearchParams(window.location.search);
	const fullPage = params.get("fullpage") != null;
	
	return <ThemeProvider theme={theme}>
		<CssBaseline/>
		
		<ToastContainer theme={mode}/>
		
		{/* router is placed outside the penta split so that it includes the sidebar, which is required to allow it to have navigation links*/}
		<BrowserRouter>
			<SlotsProvider>
				{fullPage && GlobalState.userSession == null ? <LoginPage/> : fullPage ?
					<RouterItems/> :
					<PentaSplit
						top={<Header/>}
						left={<Sidebar/>}
						center={<RouterItems/>}
						bottom={<Footer/>}
						rootAttributes={{
							style: {colorScheme: mode}
						}}
					/>
				}
			</SlotsProvider>
		</BrowserRouter>
	</ThemeProvider>;
}
